import { useMemo } from 'react';
import { create } from 'zustand';
import { mutative } from 'zustand-mutative';

import { useScopeUrl } from '@/hooks/use-scope-url';

import { markStatusDeleted, useStatusAvailabilityStore } from './status-availability';

type State = {
  statuses: Record<
    string,
    {
      expanded?: boolean;
      spoilerExpanded?: boolean;
      mediaVisible?: boolean;
      currentLanguage?: string;
      targetLanguage?: string;
      localTargetLanguage?: string;
      showPollResults?: boolean;
      showFiltered?: boolean;
    }
  >;
  actions: {
    expandStatuses: (statusIds: Array<string>) => void;
    collapseStatuses: (statusIds: Array<string>) => void;
    expandStatusSpoilers: (statusIds: Array<string>) => void;
    collapseStatusSpoilers: (statusIds: Array<string>) => void;
    revealStatusesMedia: (statusIds: Array<string>) => void;
    hideStatusesMedia: (statusIds: Array<string>) => void;
    toggleStatusesMediaHidden: (statusIds: Array<string>) => void;
    fetchTranslation: (statusId: string, targetLanguage: string) => void;
    hideTranslation: (statusId: string) => void;
    fetchLocalTranslation: (statusId: string, targetLanguage: string) => void;
    hideLocalTranslation: (statusId: string) => void;
    setStatusLanguage: (statusId: string, language: string) => void;
    toggleShowPollResults: (statusId: string) => void;
    unfilterStatus: (statusId: string) => void;
    markStatusDeleted: (statusId: string, scope: string) => void;
  };
};

const useStatusMetaStore = create<State>()(
  mutative((set) => ({
    statuses: {},
    actions: {
      expandStatuses: (statusIds) => {
        set((state: State) => {
          for (const statusId of statusIds) {
            if (!state.statuses[statusId]) state.statuses[statusId] = {};

            state.statuses[statusId].expanded = true;
          }
        });
      },
      collapseStatuses: (statusIds) => {
        set((state: State) => {
          for (const statusId of statusIds) {
            if (!state.statuses[statusId]) state.statuses[statusId] = {};

            state.statuses[statusId].expanded = false;
          }
        });
      },
      expandStatusSpoilers: (statusIds) => {
        set((state: State) => {
          for (const statusId of statusIds) {
            if (!state.statuses[statusId]) state.statuses[statusId] = {};

            state.statuses[statusId].spoilerExpanded = true;
          }
        });
      },
      collapseStatusSpoilers: (statusIds) => {
        set((state: State) => {
          for (const statusId of statusIds) {
            if (!state.statuses[statusId]) state.statuses[statusId] = {};

            state.statuses[statusId].spoilerExpanded = false;
          }
        });
      },
      revealStatusesMedia: (statusIds) => {
        set((state: State) => {
          for (const statusId of statusIds) {
            if (!state.statuses[statusId]) state.statuses[statusId] = {};

            state.statuses[statusId].mediaVisible = true;
          }
        });
      },
      hideStatusesMedia: (statusIds) => {
        set((state: State) => {
          for (const statusId of statusIds) {
            if (!state.statuses[statusId]) state.statuses[statusId] = {};

            state.statuses[statusId].mediaVisible = false;
          }
        });
      },
      toggleStatusesMediaHidden: (statusIds) => {
        set((state: State) => {
          for (const statusId of statusIds) {
            if (!state.statuses[statusId]) state.statuses[statusId] = {};

            state.statuses[statusId].mediaVisible = !state.statuses[statusId].mediaVisible;
          }
        });
      },
      fetchTranslation: (statusId, targetLanguage) => {
        set((state: State) => {
          if (!state.statuses[statusId]) state.statuses[statusId] = {};

          state.statuses[statusId].targetLanguage = targetLanguage;
        });
      },
      hideTranslation: (statusId) => {
        set((state: State) => {
          if (!state.statuses[statusId]) state.statuses[statusId] = {};

          state.statuses[statusId].targetLanguage = undefined;
        });
      },
      fetchLocalTranslation: (statusId, targetLanguage) => {
        set((state: State) => {
          if (!state.statuses[statusId]) state.statuses[statusId] = {};

          state.statuses[statusId].localTargetLanguage = targetLanguage;
        });
      },
      hideLocalTranslation: (statusId) => {
        set((state: State) => {
          if (!state.statuses[statusId]) state.statuses[statusId] = {};

          state.statuses[statusId].localTargetLanguage = undefined;
        });
      },
      setStatusLanguage: (statusId, language) => {
        set((state: State) => {
          if (!state.statuses[statusId]) state.statuses[statusId] = {};

          state.statuses[statusId].currentLanguage = language;
        });
      },
      toggleShowPollResults: (statusId) => {
        set((state: State) => {
          if (!state.statuses[statusId]) state.statuses[statusId] = {};

          state.statuses[statusId].showPollResults = !state.statuses[statusId].showPollResults;
        });
      },
      unfilterStatus: (statusId) => {
        set((state: State) => {
          if (!state.statuses[statusId]) state.statuses[statusId] = {};

          state.statuses[statusId].showFiltered = true;
        });
      },
      markStatusDeleted: (statusId, scope) => markStatusDeleted(scope, statusId),
    },
  })),
);

const emptyStatusMeta = {};

const useStatusMeta = (statusId: string) => {
  const scope = useScopeUrl();
  const meta = useStatusMetaStore((state) => state.statuses[statusId] || emptyStatusMeta);
  const deleted = useStatusAvailabilityStore((state) => !!state.deleted[scope]?.[statusId]);
  return useMemo(() => ({ ...meta, deleted }), [meta, deleted]);
};
const useStatusMetaActions = () => {
  const scope = useScopeUrl();
  const actions = useStatusMetaStore((state) => state.actions);
  return useMemo(
    () => ({
      ...actions,
      markStatusDeleted: (statusId: string) => actions.markStatusDeleted(statusId, scope),
    }),
    [actions, scope],
  );
};

export { useStatusMetaStore, useStatusMeta, useStatusMetaActions };
